import { useState, useEffect, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Calendar as CalendarComponent } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  BarChart3,
  TrendingUp,
  DollarSign,
  Users,
  Download,
  RefreshCw,
  CalendarIcon,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'
import { format, parse } from 'date-fns'
import { th } from 'date-fns/locale'

// Helper to get date range
const getDateRange = (days: number) => {
  const end = new Date()
  const start = new Date()
  start.setDate(start.getDate() - days)
  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0]
  }
}

// Mock data
const mockTeamReportData = {
  summary: {
    total_commission: 125000,
    total_members: 458,
    total_new_members: 52,
    avg_commission_per_partner: 10417,
  },
  partners: [
    { id: '1', name: 'สมชาย ใจดี', l1_commission: 25000, l2_commission: 7000, total: 32000, members: 85, new_members: 12 },
    { id: '2', name: 'วิชัย มั่งมี', l1_commission: 18500, l2_commission: 6000, total: 24500, members: 62, new_members: 8 },
    { id: '3', name: 'พิมพ์ใจ สวยงาม', l1_commission: 14000, l2_commission: 4200, total: 18200, members: 48, new_members: 6 },
    { id: '4', name: 'อนุชา รวยเร็ว', l1_commission: 12000, l2_commission: 3800, total: 15800, members: 41, new_members: 10 },
    { id: '5', name: 'สุภาพร สดใส', l1_commission: 11000, l2_commission: 3500, total: 14500, members: 38, new_members: 5 },
    { id: '6', name: 'ประยุทธ์ เก่งมาก', l1_commission: 9000, l2_commission: 3000, total: 12000, members: 35, new_members: 4 },
  ],
  daily: [
    { date: '2026-02-04', total: 8500, partners_active: 10 },
    { date: '2026-02-03', total: 7200, partners_active: 9 },
    { date: '2026-02-02', total: 9100, partners_active: 11 },
    { date: '2026-02-01', total: 6800, partners_active: 8 },
    { date: '2026-01-31', total: 7500, partners_active: 10 },
    { date: '2026-01-30', total: 8200, partners_active: 10 },
    { date: '2026-01-29', total: 6500, partners_active: 9 },
  ]
}

export function TeamReports() {
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [reportData, _setReportData] = useState(mockTeamReportData)
  
  // Suppress unused warning - will be used when connecting to real API
  void _setReportData
  const [startDate, setStartDate] = useState(getDateRange(30).start)
  const [endDate, setEndDate] = useState(getDateRange(0).end)
  const [datePreset, setDatePreset] = useState('30days')
  const [selectedPartner, setSelectedPartner] = useState<string>('all')

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 500)
    return () => clearTimeout(timer)
  }, [])

  const handleDatePreset = (preset: string) => {
    setDatePreset(preset)
    const range = preset === '7days' ? getDateRange(7) :
                  preset === '30days' ? getDateRange(30) :
                  preset === '90days' ? getDateRange(90) :
                  { start: startDate, end: endDate }
    setStartDate(range.start)
    setEndDate(range.end)
  }

  const handleRefresh = () => {
    setIsRefreshing(true)
    setTimeout(() => setIsRefreshing(false), 1000)
  }

  const filteredPartners = useMemo(() => {
    if (selectedPartner === 'all') return reportData.partners
    return reportData.partners.filter(p => p.id === selectedPartner)
  }, [reportData.partners, selectedPartner])

  const totalCommission = useMemo(() => 
    filteredPartners.reduce((acc, p) => acc + p.total, 0)
  , [filteredPartners])

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-16 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  const { summary } = reportData

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">รายงานทีม</h1>
          <p className="text-muted-foreground">สรุปรายได้และผลงานของทีม</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
            <RefreshCw className={cn("h-4 w-4 mr-2", isRefreshing && "animate-spin")} />
            รีเฟรช
          </Button>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            ส่งออก
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex gap-2">
              <Button 
                variant={datePreset === '7days' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => handleDatePreset('7days')}
              >
                7 วัน
              </Button>
              <Button 
                variant={datePreset === '30days' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => handleDatePreset('30days')}
              >
                30 วัน
              </Button>
              <Button 
                variant={datePreset === '90days' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => handleDatePreset('90days')}
              >
                90 วัน
              </Button>
            </div>
            <div className="flex items-center gap-2">
              {/* Start Date Picker */}
              <div className="space-y-1">
                <span className="text-xs text-muted-foreground">จากวันที่</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-[140px] justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(parse(startDate, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={parse(startDate, 'yyyy-MM-dd', new Date())}
                      defaultMonth={parse(startDate, 'yyyy-MM-dd', new Date())}
                      onSelect={(date) => {
                        if (date) {
                          setStartDate(format(date, 'yyyy-MM-dd'))
                          setDatePreset('custom')
                        }
                      }}
                      locale={th}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              {/* End Date Picker */}
              <div className="space-y-1">
                <span className="text-xs text-muted-foreground">ถึงวันที่</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-[140px] justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(parse(endDate, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={parse(endDate, 'yyyy-MM-dd', new Date())}
                      defaultMonth={new Date()}
                      onSelect={(date) => {
                        if (date) {
                          setEndDate(format(date, 'yyyy-MM-dd'))
                          setDatePreset('custom')
                        }
                      }}
                      locale={th}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">Partner</span>
              <Select value={selectedPartner} onValueChange={setSelectedPartner}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="เลือก Partner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">ทั้งหมด</SelectItem>
                  {reportData.partners.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-win/10">
                <DollarSign className="h-5 w-5 text-win" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">คอมมิชชั่นรวม</p>
                <p className="text-2xl font-bold text-win">{formatCurrency(totalCommission)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-blue-500/10">
                <Users className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">สมาชิกรวม</p>
                <p className="text-2xl font-bold">{summary.total_members.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-purple-500/10">
                <TrendingUp className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">สมาชิกใหม่</p>
                <p className="text-2xl font-bold">{summary.total_new_members}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-amber-500/10">
                <BarChart3 className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">เฉลี่ย/Partner</p>
                <p className="text-2xl font-bold">{formatCurrency(summary.avg_commission_per_partner)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Partner Performance Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">ผลงาน Partners</CardTitle>
          <CardDescription>รายละเอียดคอมมิชชั่นแยกตาม Partner</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4">Partner</th>
                  <th className="text-right py-3 px-4">L1</th>
                  <th className="text-right py-3 px-4">L2</th>
                  <th className="text-right py-3 px-4">รวม</th>
                  <th className="text-right py-3 px-4">สมาชิก</th>
                  <th className="text-right py-3 px-4">ใหม่</th>
                </tr>
              </thead>
              <tbody>
                {filteredPartners.map((partner, index) => (
                  <tr key={partner.id} className={cn("border-b hover:bg-muted/50", index === 0 && "bg-primary/5")}>
                    <td className="py-3 px-4 font-medium">{partner.name}</td>
                    <td className="text-right py-3 px-4">{formatCurrency(partner.l1_commission)}</td>
                    <td className="text-right py-3 px-4">{formatCurrency(partner.l2_commission)}</td>
                    <td className="text-right py-3 px-4 font-semibold text-win">{formatCurrency(partner.total)}</td>
                    <td className="text-right py-3 px-4 text-muted-foreground">{partner.members}</td>
                    <td className="text-right py-3 px-4">
                      <Badge variant="secondary" className="bg-win/10 text-win border-0">
                        +{partner.new_members}
                      </Badge>
                    </td>
                  </tr>
                ))}
                {/* Total Row */}
                <tr className="font-semibold bg-muted/30">
                  <td className="py-3 px-4">รวมทั้งหมด</td>
                  <td className="text-right py-3 px-4">
                    {formatCurrency(filteredPartners.reduce((acc, p) => acc + p.l1_commission, 0))}
                  </td>
                  <td className="text-right py-3 px-4">
                    {formatCurrency(filteredPartners.reduce((acc, p) => acc + p.l2_commission, 0))}
                  </td>
                  <td className="text-right py-3 px-4 text-win">{formatCurrency(totalCommission)}</td>
                  <td className="text-right py-3 px-4">
                    {filteredPartners.reduce((acc, p) => acc + p.members, 0)}
                  </td>
                  <td className="text-right py-3 px-4">
                    {filteredPartners.reduce((acc, p) => acc + p.new_members, 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Daily Trend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">คอมมิชชั่นรายวัน</CardTitle>
          <CardDescription>แนวโน้มคอมมิชชั่นของทีม</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {reportData.daily.map((day, index) => {
              const maxAmount = Math.max(...reportData.daily.map(d => d.total))
              const percentage = (day.total / maxAmount) * 100
              const prevDay = reportData.daily[index + 1]
              const growth = prevDay ? ((day.total - prevDay.total) / prevDay.total) * 100 : 0
              
              return (
                <div key={day.date} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{day.date}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{formatCurrency(day.total)}</span>
                      {growth !== 0 && (
                        <span className={cn(
                          "flex items-center text-xs",
                          growth > 0 ? "text-win" : "text-lose"
                        )}>
                          {growth > 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                          {Math.abs(growth).toFixed(1)}%
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary rounded-full transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
